import { BreakpointObserver, LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, contentChild, Directive, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { SidenavService, ThemeService } from '../../services';

@Directive({
    selector: '[libLayoutHeader]',
    standalone: true
})
export class LayoutHeaderDirective {
    tpl = inject(TemplateRef)
}

@Directive({
    selector: '[libLayoutNavigation]',
    standalone: true
})
export class LayoutNavigationDirective {
    tpl = inject(TemplateRef)
}

@Directive({
    selector: '[libLayoutContent]',
    standalone: true
})
export class LayoutContentDirective {
    tpl = inject(TemplateRef)
}

@Directive({
    selector: '[libLayoutFooter]',
    standalone: true
})
export class LayoutFooterDirective {
    tpl = inject(TemplateRef)
}

@Directive({
    selector: '[libLayoutRightSidenav]',
    standalone: true
})
export class LayoutRightSidenavDirective implements OnInit, OnDestroy {
    tpl = inject(TemplateRef);
    private readonly sidenavService = inject(SidenavService);

    ngOnInit() {
        this.sidenavService.setRightContent(this.tpl);
    }

    ngOnDestroy() {
        this.sidenavService.setRightContent(null);
    }
}

@Directive({
    selector: '[libLayoutLeftSidenav]',
    standalone: true
})
export class LayoutLeftSidenavDirective implements OnInit, OnDestroy {
    tpl = inject(TemplateRef);
    private readonly sidenavService = inject(SidenavService);

    ngOnInit() {
        this.sidenavService.setLeftContent(this.tpl);
    }

    ngOnDestroy() {
        this.sidenavService.setLeftContent(null);
    }
}

@Directive({
    selector: '[libLayoutBottomSidenav]',
    standalone: true
})
export class LayoutBottomSidenavDirective implements OnInit, OnDestroy {
    tpl = inject(TemplateRef);
    private readonly sidenavService = inject(SidenavService);

    ngOnInit() {
        this.sidenavService.setBottomContent(this.tpl);
    }

    ngOnDestroy() {
        this.sidenavService.setBottomContent(null);
    }
}

@Component({
    selector: 'lib-layout',
    templateUrl: './layout.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatSidenavModule,
        MatToolbarModule,
        LayoutModule,
    ],
    styleUrl: './layout.component.scss'
})
export class AppLayout implements OnInit {
    headerContent = contentChild(LayoutHeaderDirective)
    navigationContent = contentChild(LayoutNavigationDirective)
    content = contentChild(LayoutContentDirective)
    footerContent = contentChild(LayoutFooterDirective)
    rightSidenavContent = contentChild(LayoutRightSidenavDirective)
    leftSidenavContent = contentChild(LayoutLeftSidenavDirective)
    bottomSidenavContent = contentChild(LayoutBottomSidenavDirective)

    @ViewChild('contentArea', { static: true }) contentArea!: MatSidenavContent;

    sidenavService = inject(SidenavService);
    themeService = inject(ThemeService);
    breakpointObs = inject(BreakpointObserver);
    router = inject(Router);

    isMobile$ = this.themeService.breakpoints.isMobile$;

    ngOnInit() {

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            if (this.contentArea) {
                this.contentArea.scrollTo({ top: 0 });
            }
            this.sidenavService.closeAll();
        });
    }

    get sideNavMode$() {
        return this.themeService.sideNav$.pipe(map(x => x.mode));
    }

    get sideNavOpen$() {
        return this.themeService.sideNav$.pipe(map(x => x.open))
    }

    get isSidenavVisible$() {
        return this.themeService.sideNav$.pipe(
            map(({ open, mode }) => {
                // If in 'over' mode (mobile), use the open state.
                // If in 'side' mode (desktop), always show to allow for the mini-icons bar.
                return open;
            })
        );
    }
}